<template>
  <ContactForm :open="openContactForm" @hide-modal="hideContactForm()" />
  <CookieManagementModal v-model:open="openCookiePreferences" @save="onSaveCookiePreferences" />

  <!-- Footer for all brands, matching Figma desktop + mobile -->
  <div class="flex flex-col w-full" style="padding-top: 0">
    <!-- Top contact strip - only show if at least one block is available -->
    <div v-if="availableBlocks > 0" class="w-full text-white" style="background-color: var(--color-primary)">
      <div
        :class="[
          'mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 py-8 lg:py-10',
          brandconfig.BRAND === 'sporttravel'
            ? 'lg:flex-row lg:items-center lg:justify-between lg:gap-12'
            : availableBlocks === 1
              ? 'items-center justify-center'
              : availableBlocks === 2
                ? 'items-center justify-center lg:flex-row lg:justify-center lg:gap-24'
                : 'lg:flex-row lg:items-center lg:justify-between lg:gap-12',
        ]">
        <!-- Give us a call -->
        <a
          v-if="phone"
          :href="`tel:${phone.replace(/\s/g, '')}`"
          :class="[
            'flex items-start gap-4 cursor-pointer group w-full',
            availableBlocks === 3 || brandconfig.BRAND === 'sporttravel' ? 'lg:flex-1' : 'lg:w-auto',
          ]">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full contact-icon-circle">
            <PermPhoneMsgIcon :class="['h-8 w-8', shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white']" />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <div
              :class="[
                'text-base font-semibold leading-tight group-hover:underline',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              {{ t("Give us a call") }}
            </div>
            <div
              :class="[
                'mt-1 text-sm leading-tight opacity-90',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              {{ t("Call us at") }} {{ phone }}
            </div>
          </div>
        </a>

        <!-- Send us an email -->
        <div
          v-if="supportMail || isB2C"
          :class="[
            'flex items-start gap-4 cursor-pointer group w-full',
            availableBlocks === 3 || brandconfig.BRAND === 'sporttravel' ? 'lg:flex-1' : 'lg:w-auto',
          ]"
          @click="openContactForm = true">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full contact-icon-circle">
            <MarkEmailReadIcon :class="['h-8 w-8', shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white']" />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <div
              :class="[
                'text-base font-semibold leading-tight group-hover:underline',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              {{ t("Send us an email") }}
            </div>
            <div
              :class="[
                'mt-1 text-sm leading-tight opacity-90',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              <span :class="[shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white']">
                {{ t("Our support team will get back to you") }}</span
              >
            </div>
          </div>
        </div>

        <!-- Frequently Asked Questions -->
        <RouterLink
          to="/FAQ"
          :class="[
            'flex items-start gap-4 cursor-pointer group w-full',
            availableBlocks === 3 || brandconfig.BRAND === 'sporttravel' ? 'lg:flex-1' : 'lg:w-auto',
          ]">
          <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full contact-icon-circle">
            <QuizIcon :class="['h-8 w-8', shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white']" />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <div
              :class="[
                'text-base font-semibold leading-tight group-hover:underline',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              {{ t("Frequently Asked Questions") }}
            </div>
            <div
              :class="[
                'mt-1 text-sm leading-tight opacity-90',
                shouldHaveBlackTopContactText ? 'text-gray-900' : 'text-white',
              ]">
              {{ t("Find your answers quickly") }}
            </div>
          </div>
        </RouterLink>
      </div>
    </div>

    <!-- Main footer area -->
    <div :class="['w-full', shouldHaveHeaderBackgroundFooter ? 'bg-header' : 'bg-white']">
      <div class="mx-auto flex w-full max-w-screen-2xl flex-col gap-12 px-4 py-12 lg:py-16">
        <div class="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <!-- USP card with brand branding -->
          <div
            :class="[
              'relative flex w-full flex-col gap-6 rounded-2xl px-8 py-8 lg:max-w-md overflow-hidden border',
              shouldHaveHeaderBackgroundFooter ? 'border-gray-500/30' : 'border-current/20',
            ]">
            <!-- Verified user icon in background -->
            <div
              class="absolute verified-user-icon pointer-events-none"
              style="height: 165.88px; width: 158.88px; right: 1rem; top: 50%; transform: translateY(-50%)">
              <svg
                width="101"
                height="163"
                viewBox="0 0 101 163"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="h-full w-full">
                <path
                  d="M56.1752 90.5444L42.3078 76.6748C41.0115 75.38 39.4954 74.7188 37.7593 74.6912C36.0233 74.6637 34.4795 75.3249 33.1281 76.6748C31.7782 78.0262 31.1032 79.5786 31.1032 81.3321C31.1032 83.0841 31.7782 84.6358 33.1281 85.9873L50.6644 103.658C52.2393 105.232 54.0763 106.019 56.1752 106.019C58.2742 106.019 60.1111 105.232 61.686 103.658L97.7797 67.5648C99.0745 66.2686 99.7357 64.7299 99.7632 62.9489C99.7908 61.1693 99.1296 59.6038 97.7797 58.2524C96.4283 56.901 94.8759 56.2253 93.1224 56.2253C91.3703 56.2253 89.8186 56.901 88.4672 58.2524L56.1752 90.5444ZM65.32 162.88C64.4157 162.88 63.5281 162.807 62.6571 162.662C61.7862 162.517 60.9428 162.299 60.1271 162.009C41.7605 155.477 27.1477 143.896 16.2886 127.265C5.42954 110.633 0 92.6869 0 73.4262V34.2015C0 30.9065 0.955124 27.9337 2.86537 25.2832C4.77417 22.6312 7.2302 20.7079 10.2335 19.5133L59.8092 1.00593C61.6744 0.335308 63.5114 0 65.32 0C67.1286 0 68.9656 0.335308 70.8308 1.00593L120.407 19.5133C123.41 20.7079 125.866 22.6312 127.775 25.2832C129.685 27.9337 130.64 30.9065 130.64 34.2015V73.4262C130.64 92.6869 125.21 110.633 114.351 127.265C103.492 143.896 88.8795 155.477 70.5129 162.009C69.6972 162.299 68.8538 162.517 67.9829 162.662C67.1119 162.807 66.2243 162.88 65.32 162.88ZM65.32 150.068C80.4162 145.278 92.8996 135.698 102.77 121.328C112.641 106.957 117.576 90.99 117.576 73.4262V34.1166C117.576 33.5592 117.422 33.057 117.114 32.6099C116.808 32.1628 116.376 31.8275 115.817 31.604L66.241 13.0967C65.9623 12.9849 65.6553 12.929 65.32 12.929C64.9847 12.929 64.6777 12.9849 64.399 13.0967L14.8233 31.604C14.2644 31.8275 13.8319 32.1628 13.5256 32.6099C13.2179 33.057 13.064 33.5592 13.064 34.1166V73.4262C13.064 90.99 17.9993 106.957 27.8699 121.328C37.7404 135.698 50.2238 145.278 65.32 150.068Z"
                  fill="#9CA3AF" />
              </svg>
            </div>

            <!-- Logo -->
            <div class="relative z-[1]">
              <div
                v-if="logoFooter"
                :class="[
                  'inline-block',
                  brandconfig.BRAND === 'racesport' ? 'bg-header px-1 py-0.75 rounded' : '',
                  shouldMakeLogoBlue ? 'logo-blue-wrapper' : '',
                ]"
                :style="
                  shouldMakeLogoBlue && logoFooter
                    ? {
                        '--logo-src': `url(${typeof logoFooter === 'string' ? logoFooter : logoFooter.src || logoFooter.default || logoFooter})`,
                      }
                    : {}
                ">
                <img
                  :src="logoFooter"
                  :alt="brandName"
                  :class="[
                    'h-8 w-auto object-contain',
                    shouldInvertLogo ? 'logo-invert' : '',
                    shouldMakeLogoBlue ? 'logo-blue-img' : '',
                  ]" />
              </div>
              <div
                v-else
                :class="[
                  'text-2xl font-bold italic',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                <span class="font-bold italic">{{ brandName }}</span
                ><span v-if="showBrandSuffix" class="text-lg font-bold italic">{{ brandSuffix }}</span>
              </div>
            </div>
            <ul class="flex flex-col gap-4 text-lg font-normal leading-[1.5] relative z-[1]">
              <li v-for="(usp, index) in topBarUsps" :key="index" class="flex items-start gap-3">
                <div
                  :class="[
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                    checkIconBgClass,
                    checkIconBorderClass,
                  ]">
                  <CheckIcon :class="['h-3.5 w-3.5 stroke-2', checkIconColorClass]" />
                </div>
                <span
                  :class="[
                    'font-normal',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  >{{ t(usp.text) }}</span
                >
              </li>
            </ul>
          </div>

          <!-- Categories and Customer Service wrapper - only on smaller screens -->
          <div class="flex flex-row flex-nowrap gap-6 w-full lg:hidden">
            <!-- Categories -->
            <div class="flex-1 min-w-0">
              <h4
                :class="[
                  'mb-4 text-lg font-bold',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                {{ t("Categories") }}
              </h4>
              <ul
                :class="[
                  'flex flex-col gap-3 text-base',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                <li v-for="item in menuCategories" :key="item.label" class="flex items-center gap-2">
                  <ArrowForwardIosIcon
                    :class="[
                      'h-3 w-3',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]" />
                  <RouterLink
                    :to="item.flyout_link?.to || item.to || '/events'"
                    :class="[
                      'hover:underline font-normal',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    >{{ t(item.label) }}</RouterLink
                  >
                </li>
                <li class="flex items-center gap-2">
                  <ArrowForwardIosIcon
                    :class="[
                      'h-3 w-3',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]" />
                  <RouterLink
                    to="/events"
                    :class="[
                      'hover:underline font-normal',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    >{{ t("All categories") }}</RouterLink
                  >
                </li>
              </ul>
            </div>

            <!-- Customer service -->
            <div class="flex-1 min-w-0">
              <h4
                :class="[
                  'mb-4 text-lg font-bold',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                {{ t("Support") }}
              </h4>
              <ul
                :class="[
                  'flex flex-col gap-3 text-base',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                <li class="flex items-center gap-2">
                  <ArrowForwardIosIcon
                    :class="[
                      'h-3 w-3',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]" />
                  <span
                    :class="[
                      'cursor-pointer font-normal hover:underline',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    @click="openContactForm = true"
                    >{{ t("Contact support") }}</span
                  >
                </li>
                <li class="flex items-center gap-2">
                  <ArrowForwardIosIcon
                    :class="[
                      'h-3 w-3',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]" />
                  <RouterLink
                    to="/FAQ"
                    :class="[
                      'font-normal hover:underline',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    >{{ t("FAQ") }}</RouterLink
                  >
                </li>
                <li v-if="isB2C" class="flex items-center gap-2">
                  <ArrowForwardIosIcon
                    :class="[
                      'h-3 w-3',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]" />
                  <span
                    :class="[
                      'cursor-pointer font-normal hover:underline',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    @click="openCookiePreferences = true">
                    {{ t("Cookie preferences") }}
                  </span>
                </li>
              </ul>

              <div
                v-if="
                  (phone || (supportMail && brandconfig.BRAND !== 'sporttravel')) &&
                  (!isB2C || brandconfig.BRAND === 'sporttravel')
                "
                class="mt-6 flex flex-col gap-4 text-base">
                <div v-if="phone" class="flex items-center gap-3">
                  <div
                    :class="[
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                      iconCircleBgClass,
                      iconCircleBorderClass,
                    ]">
                    <PermPhoneMsgIconFilled class="h-6 w-6 text-white" />
                  </div>
                  <a
                    :href="`tel:${phone.replace(/\s/g, '')}`"
                    :class="[
                      'font-normal hover:underline',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    >{{ phone }}</a
                  >
                </div>
                <div v-if="supportMail && brandconfig.BRAND !== 'sporttravel'" class="flex items-center gap-3 min-w-0">
                  <div
                    :class="[
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                      iconCircleBgClass,
                      iconCircleBorderClass,
                    ]">
                    <MarkEmailReadIconFilled v-if="brandconfig.BRAND === 'xs2event'" class="h-6 w-6 text-white" />
                    <MarkEmailReadIcon v-else class="h-6 w-6 text-white" />
                  </div>
                  <span
                    class="min-w-0 break-all"
                    :class="[
                      'font-normal hover:underline cursor-pointer',
                      shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                    ]"
                    @click="openContactForm = true"
                    >{{ supportMail }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Categories - separate on large screens -->
          <div class="hidden lg:block w-auto lg:min-w-[200px]">
            <h4
              :class="[
                'mb-4 text-lg font-bold',
                shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
              ]">
              {{ t("Categories") }}
            </h4>
            <ul
              :class="[
                'flex flex-col gap-3 text-base',
                shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
              ]">
              <li v-for="item in menuCategories" :key="item.label" class="flex items-center gap-2">
                <ArrowForwardIosIcon
                  :class="['h-3 w-3', shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900']" />
                <RouterLink
                  :to="item.flyout_link?.to || item.to || '/events'"
                  :class="[
                    'hover:underline font-normal',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  >{{ t(item.label) }}</RouterLink
                >
              </li>
              <li class="flex items-center gap-2">
                <ArrowForwardIosIcon
                  :class="['h-3 w-3', shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900']" />
                <RouterLink
                  to="/events"
                  :class="[
                    'hover:underline font-normal',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  >{{ t("All categories") }}</RouterLink
                >
              </li>
            </ul>
          </div>

          <!-- Customer service - separate on large screens -->
          <div class="hidden lg:block w-auto lg:min-w-[240px]">
            <h4
              :class="[
                'mb-4 text-lg font-bold',
                shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
              ]">
              {{ t("Support") }}
            </h4>
            <ul
              :class="[
                'flex flex-col gap-3 text-base',
                shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
              ]">
              <li class="flex items-center gap-2">
                <ArrowForwardIosIcon
                  :class="['h-3 w-3', shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900']" />
                <span
                  :class="[
                    'cursor-pointer font-normal hover:underline',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  @click="openContactForm = true"
                  >{{ t("Contact support") }}</span
                >
              </li>
              <li class="flex items-center gap-2">
                <ArrowForwardIosIcon
                  :class="['h-3 w-3', shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900']" />
                <RouterLink
                  to="/FAQ"
                  :class="[
                    'font-normal hover:underline',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  >{{ t("FAQ") }}</RouterLink
                >
              </li>
              <li v-if="isB2C" class="flex items-center gap-2">
                <ArrowForwardIosIcon
                  :class="['h-3 w-3', shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900']" />
                <span
                  :class="[
                    'cursor-pointer font-normal hover:underline',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  @click="openCookiePreferences = true">
                  {{ t("Cookie preferences") }}
                </span>
              </li>
            </ul>

            <div
              v-if="
                (phone || (supportMail && brandconfig.BRAND !== 'sporttravel')) &&
                (!isB2C || brandconfig.BRAND === 'sporttravel')
              "
              class="mt-6 flex flex-col gap-4 text-base">
              <div v-if="phone" class="flex items-center gap-3">
                <div
                  :class="[
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    iconCircleBgClass,
                    iconCircleBorderClass,
                  ]">
                  <PermPhoneMsgIconFilled class="h-6 w-6 text-white" />
                </div>
                <a
                  :href="`tel:${phone.replace(/\s/g, '')}`"
                  :class="[
                    'font-normal hover:underline',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  >{{ phone }}</a
                >
              </div>
              <div v-if="supportMail && brandconfig.BRAND !== 'sporttravel'" class="flex items-center gap-3">
                <div
                  :class="[
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    iconCircleBgClass,
                    iconCircleBorderClass,
                  ]">
                  <MarkEmailReadIconFilled v-if="brandconfig.BRAND === 'xs2event'" class="h-6 w-6 text-white" />
                  <MarkEmailReadIcon v-else class="h-6 w-6 text-white" />
                </div>
                <span
                  :class="[
                    'font-normal hover:underline cursor-pointer',
                    shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                  ]"
                  @click="openContactForm = true"
                  >{{ supportMail }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Separator -->
        <hr
          v-if="!shouldHaveBlackBottomSection"
          :class="['', shouldHaveHeaderBackgroundFooter ? 'border-gray-500/30' : 'border-gray-200']" />

        <!-- Brand bar + legal + payment logos - inside container for normal brands -->
        <div v-if="!shouldHaveBlackBottomSection" class="flex flex-col items-center gap-6">
          <!-- Logo and tagline -->
          <div class="flex flex-col items-center gap-2">
            <div
              v-if="logoFooter"
              :class="[shouldMakeLogoBlue ? 'logo-blue-wrapper' : '']"
              :style="
                shouldMakeLogoBlue && logoFooter
                  ? {
                      '--logo-src': `url(${typeof logoFooter === 'string' ? logoFooter : logoFooter.src || logoFooter.default || logoFooter})`,
                    }
                  : {}
              ">
              <img
                :src="logoFooter"
                :alt="brandName"
                :class="[
                  'h-10 w-auto object-contain',
                  shouldInvertLogo ? 'logo-invert' : '',
                  shouldMakeLogoBlue ? 'logo-blue-img' : '',
                ]" />
            </div>
            <div
              v-else
              :class="[
                'text-2xl font-bold italic',
                shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
              ]">
              <span class="font-bold italic">{{ brandName }}</span
              ><span v-if="showBrandSuffix" class="text-lg font-bold italic">{{ brandSuffix }}</span>
            </div>
            <div v-if="brandTagline" class="tagline-banner">
              {{ brandTagline }}
            </div>
          </div>

          <!-- Legal links - only show if provided -->
          <div
            v-if="legalLinks && legalLinks.length > 0"
            :class="[
              'flex flex-wrap items-center justify-center gap-4 text-sm',
              shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
            ]">
            <template v-for="(link, index) in legalLinks" :key="index">
              <a
                v-if="link.href"
                :href="link.href"
                :class="[
                  'hover:underline',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]"
                target="_blank"
                rel="noopener noreferrer">
                {{ link.label }}
              </a>
              <RouterLink
                v-else-if="link.to"
                :to="link.to"
                :class="[
                  'hover:underline',
                  shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
                ]">
                {{ link.label }}
              </RouterLink>
            </template>
          </div>

          <!-- Copyright -->
          <div
            :class="[
              'flex flex-col items-center gap-4 text-sm',
              shouldHaveHeaderBackgroundFooter ? 'text-header-foreground' : 'text-gray-900',
            ]">
            <span v-if="brandconfig.BRAND === 'xs2event'"
              >© {{ new Date().getFullYear() }} XS2Event B.V. All rights reserved.</span
            >
            <span v-else>© {{ new Date().getFullYear() }} {{ brandconfig.BRAND_TITLE }} Ticketshop</span>

            <!-- Payment logos - only show here if NOT in USP card -->
            <div v-if="!shouldShowPaymentLogosInUSP" class="flex flex-wrap items-center justify-center gap-3">
              <div
                v-for="logo in paymentLogos"
                :key="logo.alt"
                class="flex h-8 items-center rounded border border-gray-200 bg-white px-2 py-1">
                <img class="h-5 w-auto object-contain" :src="logo.url" :alt="logo.alt" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom section - full width, extends down for specific brands -->
    <div
      v-if="shouldHaveBlackBottomSection"
      :class="[
        'w-full',
        brandconfig.BRAND === 'racesport' ? 'bg-white text-gray-900' : 'bg-header text-header-foreground',
      ]">
      <!-- Separator -->
      <hr :class="[brandconfig.BRAND === 'racesport' ? 'border-gray-200' : 'border-white/20']" />
      <div class="mx-auto flex w-full max-w-screen-2xl flex-col items-center justify-center gap-6 px-4 py-16 lg:py-24">
        <!-- Logo and tagline -->
        <div class="flex flex-col items-center gap-2">
          <div
            v-if="logoFooter"
            :class="[
              'inline-block',
              brandconfig.BRAND === 'racesport' ? 'bg-header px-1 py-0.75 rounded' : '',
              shouldMakeLogoBlue ? 'logo-blue-wrapper' : '',
            ]"
            :style="
              shouldMakeLogoBlue && logoFooter
                ? {
                    '--logo-src': `url(${typeof logoFooter === 'string' ? logoFooter : logoFooter.src || logoFooter.default || logoFooter})`,
                  }
                : {}
            ">
            <img
              :src="logoFooter"
              :alt="brandName"
              :class="[
                'h-10 w-auto object-contain',
                shouldInvertLogo ? 'logo-invert' : '',
                shouldMakeLogoBlue ? 'logo-blue-img' : '',
              ]" />
          </div>
          <div
            v-else
            :class="[
              'text-2xl font-bold italic',
              brandconfig.BRAND === 'racesport' ? 'text-gray-900' : 'text-header-foreground',
            ]">
            <span class="font-bold italic">{{ brandName }}</span
            ><span v-if="showBrandSuffix" class="text-lg font-bold italic">{{ brandSuffix }}</span>
          </div>
          <div v-if="brandTagline" class="tagline-banner">
            {{ brandTagline }}
          </div>
        </div>

        <!-- Legal links - only show if provided -->
        <div
          v-if="legalLinks && legalLinks.length > 0"
          :class="[
            'flex flex-wrap items-center justify-center gap-4 text-sm',
            brandconfig.BRAND === 'racesport' ? 'text-gray-900' : 'text-header-foreground',
          ]">
          <template v-for="(link, index) in legalLinks" :key="index">
            <a
              v-if="link.href"
              :href="link.href"
              :class="[
                'hover:underline',
                brandconfig.BRAND === 'racesport' ? 'text-gray-900' : 'text-header-foreground',
              ]"
              target="_blank"
              rel="noopener noreferrer">
              {{ link.label }}
            </a>
            <RouterLink
              v-else-if="link.to"
              :to="link.to"
              :class="[
                'hover:underline',
                brandconfig.BRAND === 'racesport' ? 'text-gray-900' : 'text-header-foreground',
              ]">
              {{ link.label }}
            </RouterLink>
          </template>
        </div>

        <!-- Copyright -->
        <div
          :class="[
            'flex flex-col items-center gap-4 text-sm',
            brandconfig.BRAND === 'racesport' ? 'text-gray-900' : 'text-header-foreground',
          ]">
          <span v-if="brandconfig.BRAND === 'xs2event'"
            >© {{ new Date().getFullYear() }} XS2Event B.V. All rights reserved.</span
          >
          <span v-else>© {{ new Date().getFullYear() }} {{ brandconfig.BRAND_TITLE }} Ticketshop</span>

          <!-- Payment logos - moved from USP card to bottom section -->
          <div v-if="shouldShowPaymentLogosInUSP" class="flex flex-wrap items-center justify-center gap-3">
            <div
              v-for="logo in paymentLogos"
              :key="logo.alt"
              class="flex h-8 items-center rounded border border-gray-200 bg-white px-2 py-1">
              <img class="h-5 w-auto object-contain" :src="logo.url" :alt="logo.alt" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import brandconfig, { isB2C } from "@/brandconfig";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import ContactForm from "@/components/ContactForm.vue";
import CookieManagementModal from "@/components/shared/CookieManagementModal.vue";
import PermPhoneMsgIcon from "~icons/material-symbols/perm-phone-msg-outline";
import PermPhoneMsgIconFilled from "~icons/material-symbols/perm-phone-msg";
import MarkEmailReadIcon from "~icons/material-symbols/mark-email-read-outline";
import MarkEmailReadIconFilled from "~icons/material-symbols/mark-email-read";
import QuizIcon from "~icons/material-symbols/quiz-outline";
import ArrowForwardIosIcon from "~icons/material-symbols/arrow-forward-ios";
import { CheckIcon } from "@heroicons/vue/24/outline";
import iDealLogo from "@/assets/images/payment-methods/ideal.svg";
import applyPayLogo from "@/assets/images/payment-methods/apple_pay.svg";
import americanExpressLogo from "@/assets/images/payment-methods/american_express.svg";
import masterCardLogo from "@/assets/images/payment-methods/master_card.svg";
import visaLogo from "@/assets/images/payment-methods/visa.svg";

const paymentLogos = [
  { url: iDealLogo, alt: "iDeal" },
  { url: applyPayLogo, alt: "Apple Pay" },
  { url: americanExpressLogo, alt: "American Express" },
  { url: masterCardLogo, alt: "Master Card" },
  { url: visaLogo, alt: "Visa" },
];

const { t } = useI18n();

const openContactForm = ref(false);
const openCookiePreferences = ref(false);

const onSaveCookiePreferences = (preferences) => {
  try {
    localStorage.setItem("cookiePreferences", JSON.stringify(preferences));
    // Keep cookieConsent in sync so the banner doesn't re-appear.
    localStorage.setItem("cookieConsent", preferences?.analytics ? "accepted" : "declined");
  } catch (e) {
    console.error("Failed to store cookie preferences", e);
  }

  openCookiePreferences.value = false;
  window.location.reload();
};

const hideContactForm = () => {
  openContactForm.value = false;
};

// Phone numbers are only for sporttravel and xs2event
const phone = computed(() => {
  if (brandconfig.BRAND === "sporttravel") {
    return "+442046381450";
  }
  if (brandconfig.BRAND === "xs2event") {
    return "+31850045378";
  }
  // Don't show phone for GPfans even if configured
  if (brandconfig.BRAND === "gpfans") {
    return "";
  }
  return brandconfig.footer?.contact?.phone || "";
});

// Email - only show if specified
const supportMail = computed(() => {
  return brandconfig.footer?.contact?.support_mail || "";
});

// Count available blocks for centering logic
const availableBlocks = computed(() => {
  // For B2C shops, always show the contact strip (at least email and FAQ)
  if (isB2C) {
    return 2; // Email (opens contact form) and FAQ
  }
  let count = 0;
  if (phone.value) count++;
  if (supportMail.value) count++;
  count++; // FAQ is always available
  return count;
});

// Check if top contact strip text should be black (for xs2event, demoshop, and voetbalzone)
const shouldHaveBlackTopContactText = computed(() => {
  return brandconfig.BRAND === "xs2event" || brandconfig.BRAND === "demoshop" || brandconfig.BRAND === "voetbalzone";
});

// Check if header is white/light colored - if so, use black for icon circles
const isHeaderWhite = computed(() => {
  // Check computed style of header color
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    const headerColor = getComputedStyle(root).getPropertyValue("--color-header").trim();
    // Check if it's white or very light color
    if (
      headerColor === "#fff" ||
      headerColor === "#ffffff" ||
      headerColor === "white" ||
      headerColor === "#f3f5fa" ||
      headerColor === "#fafafa" ||
      headerColor.startsWith("rgb(255")
    ) {
      return true;
    }
  }
  return false;
});

// Icon circle background class - use header color, but black if header is white
const iconCircleBgClass = computed(() => {
  return isHeaderWhite.value ? "bg-gray-900" : "bg-header";
});

// Brand name display - use BRAND_SHORT_TITLE if available, otherwise BRAND_TITLE
const brandName = computed(() => {
  return brandconfig.BRAND_SHORT_TITLE || brandconfig.BRAND_TITLE || brandconfig.BRAND || "";
});

// Brand suffix (e.g., ".com") - from brandconfig or extracted from title
const title = brandconfig.BRAND_TITLE || brandconfig.BRAND_SHORT_TITLE || "";
const titleSuffixMatch = title.match(/\.(com|net|org|io)/);
const brandSuffix = brandconfig.BRAND_SUFFIX || (titleSuffixMatch ? titleSuffixMatch[0] : "");
const showBrandSuffix = !!brandSuffix;

// Brand tagline - from brandconfig footer tagline
const brandTagline = brandconfig.footer?.tagline || null;

// USPs from top bar (HeaderTopBar) - from brandconfig
const topBarUsps = brandconfig.usps || [];

// Logo - use the same logo as the header
const logoFooter = brandconfig.LOGO;

// Check if logo should be inverted (if it's white/light colored)
const shouldInvertLogo = computed(() => {
  if (!logoFooter) return false;

  // Don't invert logos for priority brand
  if (brandconfig.BRAND === "priority") {
    return false;
  }

  // SportTravel logo should be blue (primary color), not inverted
  if (brandconfig.BRAND === "sporttravel") {
    return false;
  }
  // Check if logo path/name suggests it's white (for string paths)
  if (typeof logoFooter === "string") {
    const logoPath = logoFooter.toLowerCase();
    return logoPath.includes("white") || logoPath.includes("_white");
  }

  // For imported modules, try to get the src/url
  if (logoFooter && typeof logoFooter === "object") {
    const src = logoFooter.src || logoFooter.default || logoFooter;
    if (typeof src === "string") {
      return src.toLowerCase().includes("white") || src.toLowerCase().includes("_white");
    }
  }

  return false;
});

// Check if logo should be blue (for SportTravel)
const shouldMakeLogoBlue = computed(() => {
  return brandconfig.BRAND === "sporttravel";
});

// Legal links - only show for xs2event brand, excluding "Partnerships and cooperations"
const legalLinks = computed(() => {
  if (brandconfig.BRAND === "xs2event") {
    const links = brandconfig.footer?.about || [];
    // Filter out "Partnerships and cooperations" link
    return links.filter(
      (link) =>
        !link.label?.toLowerCase().includes("partnerships") && !link.label?.toLowerCase().includes("cooperations")
    );
  }
  return [];
});

// Categories from menu - show all menu items, not just specific ones
const menuCategories = computed(() => {
  if (!brandconfig.menu) return [];
  // Return all menu items that have a label and are not "All events"
  return brandconfig.menu.filter((item) => item.label && item.label !== "All events");
});

// Brands that should show payment logos in USP card and have black bottom section
const brandsWithSpecialFooterLayout = ["gpfans", "fcupdate", "motor", "racesport"];

// Check if payment logos should be shown in USP card
const shouldShowPaymentLogosInUSP = computed(() => {
  return brandsWithSpecialFooterLayout.includes(brandconfig.BRAND);
});

// Check if bottom section should have black background
const shouldHaveBlackBottomSection = computed(() => {
  return brandsWithSpecialFooterLayout.includes(brandconfig.BRAND);
});

// Check icon background class - always black
const checkIconBgClass = "bg-gray-900";

// Check icon color class - always white
const checkIconColorClass = "text-white";

// Check icon border class - white border for brands with header background
const checkIconBorderClass = computed(() => {
  if (shouldHaveHeaderBackgroundFooter.value) {
    return "border-2 border-white";
  }
  return "";
});

// Brands that should have header background in main footer area
const brandsWithHeaderBackgroundFooter = ["priority", "fcupdate", "motor", "gpfans"];

// Check if main footer area should have header background
const shouldHaveHeaderBackgroundFooter = computed(() => {
  return brandsWithHeaderBackgroundFooter.includes(brandconfig.BRAND);
});

// Icon circle border class - white border for brands with header background
const iconCircleBorderClass = computed(() => {
  if (shouldHaveHeaderBackgroundFooter.value) {
    return "border-2 border-white";
  }
  return "";
});
</script>

<style scoped>
:root {
  --logo-src: none;
}

.verified-user-icon :deep(svg) {
  fill: #9ca3af !important;
  opacity: 0.2 !important;
}

.verified-user-icon :deep(svg path) {
  fill: #9ca3af !important;
}

/* Invert white logos to black */
.logo-invert {
  filter: invert(1);
}

/* Make SportTravel logo blue using primary color variable (button color) */
.logo-blue-wrapper {
  position: relative;
  display: inline-block;
}

.logo-blue-wrapper .logo-blue-img {
  /* Hide the original white logo */
  opacity: 0;
}

.logo-blue-wrapper::after {
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  /* Use the image as a mask */
  mask-image: var(--logo-src);
  -webkit-mask-image: var(--logo-src);
  mask-size: contain;
  -webkit-mask-size: contain;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-position: center;
  /* Apply primary color (button color) - use the CSS variable */
  background-color: var(--color-primary);
  pointer-events: none;
  z-index: 1;
}

/* Contact strip icon circles - use primary color with 80% opacity */
.contact-icon-circle {
  background-color: var(--color-primary);
  opacity: 1;
}

/* Tagline banner styling - "FROM FANS, FOR FANS!" - matching design specs */
.tagline-banner {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 8px;
  gap: 8px;
  width: 201px;
  height: 24px;
  background: #ff4d70;
  font-family: "Inter", sans-serif;
  font-style: italic;
  font-weight: 700;
  font-size: 16px;
  line-height: 150%; /* identical to box height, or 24px */
  color: #ffffff;
  flex: none;
  order: 0;
  flex-grow: 0;
  white-space: nowrap;
}
</style>
